import React, { useContext, useMemo } from 'react';
import ProductPageContext from '../../contexts/ProductPageContext';
import buildQueryString from '../../tools/buildQueryString';

const getTmntDataByType = {
  classic: ['pdpFit', 'pdpWomenClassicDescription', 'pdpClassic', 'pdpAlsoAvailable', 'pdpCurveLoveFit'],
  curve: ['pdpFit', 'pdpCurveLoveDescription', 'pdpCurveLove', 'pdpAlsoAvailable', 'pdpClassicFit'],
};

const getElevatedFitTextData = (fitType, tmntData) => {
  if (!fitType || !tmntData) return null;

  const normalizedFitType = fitType.toLowerCase();
  const textKeys = getTmntDataByType[normalizedFitType];
  if (!textKeys) return null;

  return {
    fitText: tmntData[textKeys[0]]?.value || '',
    fitDescription: tmntData[textKeys[1]]?.value || '',
    fitType: tmntData[textKeys[2]]?.value || '',
    alsoInText: tmntData[textKeys[3]]?.value || '',
    fitOpposite: tmntData[textKeys[4]]?.value || '',
  };
};

const getOppositeUrl = (currentFitName, seoKeyword) => {
  if (!currentFitName || !seoKeyword) return '';

  // Build the opposite URL based on the current fit type
  const currentFitType = currentFitName.toLowerCase();
  const oppositeFitType = currentFitType === 'classic' ? 'curve-love' : 'classic';

  // Construct the URL - this might need adjustment based on your URL structure
  return `/womens/${seoKeyword}/${oppositeFitType}/`;
};

export const getUrlWithQueryParams = (elevatedFit) => {
  if (!elevatedFit?.name || !elevatedFit?.seoKeyword) return '';

  const baseUrl = getOppositeUrl(elevatedFit.name, elevatedFit.seoKeyword);

  return `${baseUrl}?${buildQueryString({
    seq: elevatedFit?.defaultSwatchSequence,
    pagefm: elevatedFit?.name?.toLowerCase(),
  })}`;
};

export default function ElevatedFit({ activeProduct }) {
  const ProductPageState = useContext(ProductPageContext);
  const { tmntData } = ProductPageState;
  const { elevatedFit } = activeProduct || {};

  const elevatedFitTextData = useMemo(() => getElevatedFitTextData(elevatedFit?.name, tmntData), [elevatedFit?.name, tmntData]);

  const combinedElevatedFit = useMemo(() => ({
    ...elevatedFit,
    ...elevatedFitTextData,
  }), [elevatedFit, elevatedFitTextData]);

  const urlWithQueryParams = useMemo(() => getUrlWithQueryParams(
    combinedElevatedFit,
  ), [combinedElevatedFit]);

  if (!elevatedFit) return null;

  return (
    <div className="elevated-fit-container" data-testid="elevated-fit">
      <span className="current-fit">
        <span className="fit">
          {combinedElevatedFit?.fitText}
          :
        </span>
        {' '}
        {combinedElevatedFit?.fitType}
      </span>

      <p className="description">
        {combinedElevatedFit?.fitDescription}
      </p>

      <span className="opposite">
        {combinedElevatedFit?.alsoInText}
        {' '}
        <a href={urlWithQueryParams} className="opposite-url">{combinedElevatedFit?.fitOpposite}</a>
        .
      </span>
    </div>
  );
}
